// UI text by key. Call t() at render time or in a handler, never at module level: a language change re-renders.
import { createElement, Fragment } from 'react';
import { Bdi } from './direction';
import CATALOGS from './catalogs';

const { en } = CATALOGS;
let language = 'en';
let listener = null;

const hasCatalog = code => Object.keys(CATALOGS).includes(code);
const readChoice = () => { try { return localStorage.getItem('uiLanguage'); } catch (e) { return null; } };

// The stored choice; else the first browser language (`he` of `he-IL`) that has a catalog; else English.
const firstLanguage = () => {
  const choice = readChoice();
  if (hasCatalog(choice)) return choice;
  return (navigator.languages || []).map(tag => String(tag).split('-')[0].toLowerCase()).find(hasCatalog) ?? 'en';
};

// A plural text is an object of Intl.PluralRules categories; a missing category falls back to `other`.
const form = (text, count) =>
  typeof text === 'object' ? text[new Intl.PluralRules(language).select(count)] ?? text.other : text;

// Named values fill {name} slots; `count` also picks the plural form. A slot with no value stays as it is.
export const t = (key, values = {}) =>
  form(CATALOGS[language][key] ?? en[key], values.count)
    .replace(/\{(\w+)\}/g, (all, name) => (name in values ? String(values[name]) : all));

// The text of t for the screen: each string value in a <bdi> (Bdi), so a name keeps its own direction and does not move
// the words, digits or punctuation around it. Accessible names stay plain strings from t.
export const tx = (key, values = {}) => createElement(Fragment, null,
  ...form(CATALOGS[language][key] ?? en[key], values.count).split(/\{(\w+)\}/).map((part, i) => {
    if (i % 2 === 0) return part;
    if (!(part in values)) return `{${part}}`;
    return typeof values[part] === 'string' ? createElement(Bdi, null, values[part]) : String(values[part]);
  }));

// Runs before the first render with the first language, and on a selection with its code: sets the page language,
// direction and tab title. An unknown code means English.
export function loadLanguage(code = firstLanguage()) {
  language = hasCatalog(code) ? code : 'en';
  document.documentElement.lang = language;
  document.documentElement.dir = CATALOGS[language]['language.dir'];
  document.title = t('app.title');
}

// Every language that has a catalog, under its own name, and whether it is the current one.
export const languages = () =>
  Object.entries(CATALOGS).map(([code, c]) => ({ code, name: c['language.name'], current: code === language }));

// Game re-renders the screens on a change; one listener at a time.
export const onLanguageChange = fn => { listener = fn; };

// A selection in the language menu: kept on this device only, and shown at once with no reload.
export function setLanguage(code) {
  try { localStorage.setItem('uiLanguage', code); } catch (e) { /* storage refused: the change holds for this visit */ }
  loadLanguage(code);
  listener?.();
}
